import { useCallback, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { checkAccess } from '../service/access'
import useAuth from '../models/useAuth'

// Fallback poll in case realtime drops
const FALLBACK_POLL_INTERVAL = 30000

type Options = {
  onGranted: () => void
  onRejected: () => void
}

/**
 * Watches an in-flight access request for the current user. Listens to
 * access_grants realtime events filtered to the requester's email, and also
 * falls back to polling `checkAccess` in case realtime drops.
 *
 * Both verdicts arrive as UPDATEs: approval sets granted_at, rejection sets
 * rejected_at. The DELETE arm is kept for a row removed some other way — an
 * older tab still deleting on reject, or console SQL — where "this is not
 * happening" is the honest reading. DELETE events require REPLICA IDENTITY FULL
 * on access_grants so the old row carries requester_email for server-side
 * filtering.
 */
export default function useAccessRequestWatcher(serviceName: string | undefined, { onGranted, onRejected }: Options) {
  const email = useAuth(state => state.user?.email ?? '')
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null)
  const serviceIdRef = useRef<string | null>(null)

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
      channelRef.current = null
    }
  }, [])

  const start = useCallback(async () => {
    if (!serviceName || !email) return
    stop()

    // Resolve the service id so we can scope realtime events to this service only.
    const initial = await checkAccess(serviceName)
    serviceIdRef.current = initial?.serviceId ?? null

    channelRef.current = supabase
      .channel(`access_grants-self-${serviceName}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'access_grants',
          filter: `requester_email=eq.${email}`,
        },
        async payload => {
          const row = payload.new as { granted_at: string | null; rejected_at: string | null; service_id?: string }
          if (serviceIdRef.current && row.service_id && row.service_id !== serviceIdRef.current) return
          // Without the service id, the event can be about ANY of this requester's
          // grants. A grant is still safe to act on (it is re-checked below); a
          // rejection is not, because it would tell the visitor this service
          // refused them when another one did. A reload shows a real rejection.
          const scoped = serviceIdRef.current !== null && row.service_id === serviceIdRef.current

          // A rejection is an UPDATE now — it has to survive the row so a reload cannot re-ask — so
          // this arm carries both verdicts. granted_at wins: approving after a rejection clears it.
          if (!row.granted_at) {
            // Deliberately no stop(): a rejection is reversible from the owner's side, and this page is
            // where the requester is sitting if they change their mind.
            if (row.rejected_at && scoped) onRejected()
            return
          }

          const result = await checkAccess(serviceName)
          if (result?.authorized) {
            stop()
            onGranted()
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'access_grants',
          filter: `requester_email=eq.${email}`,
        },
        payload => {
          const row = payload.old as { service_id?: string }
          // Requires REPLICA IDENTITY FULL on access_grants so service_id is present.
          // Unscoped, a DELETE can be any of this requester's grants (see above).
          if (serviceIdRef.current === null || row.service_id !== serviceIdRef.current) return
          stop()
          onRejected()
        }
      )
      .subscribe()

    intervalRef.current = setInterval(async () => {
      const result = await checkAccess(serviceName)
      if (result?.authorized) {
        stop()
        onGranted()
      }
    }, FALLBACK_POLL_INTERVAL)
  }, [serviceName, email, onGranted, onRejected, stop])

  useEffect(() => stop, [stop])

  return { start, stop }
}
